import { useMemo } from 'react'
import { useReadContracts } from 'wagmi'
import type { Address } from 'viem'
import { CONTRACTS, CONTRACTS_CHAIN_ID, erc20Abi } from '@/lib/contracts'
import { formatAmount, formatCompact } from '@/lib/format'

/**
 * Token identity, read from the tokens themselves.
 *
 * Name, symbol and decimals were hardcoded as "NDX", "USDT" and 18 throughout the
 * UI. Two problems with that: the labels silently lie if a contract is redeployed
 * with different metadata, and the decimals assumption is not merely cosmetic — real
 * USDT has 6 decimals on most chains, so a hardcoded 18 would render every balance
 * a trillion times too small with no error anywhere.
 *
 * Metadata is immutable, so this is cached for the session rather than polled.
 */

const chainId = CONTRACTS_CHAIN_ID

export interface TokenMeta {
  address: Address
  name: string
  symbol: string
  decimals: number
}

const FALLBACK = (address: Address, symbol: string): TokenMeta => ({
  address,
  name: symbol,
  symbol,
  decimals: 18,
})

export function useTokens() {
  const query = useReadContracts({
    contracts: [
      { address: CONTRACTS.ndx, abi: erc20Abi, functionName: 'symbol', chainId },
      { address: CONTRACTS.ndx, abi: erc20Abi, functionName: 'decimals', chainId },
      { address: CONTRACTS.usdt, abi: erc20Abi, functionName: 'symbol', chainId },
      { address: CONTRACTS.usdt, abi: erc20Abi, functionName: 'decimals', chainId },
    ],
    query: {
      // Token metadata cannot change, so refetching it is pure waste.
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnWindowFocus: false,
    },
  })

  return useMemo(() => {
    const at = <T,>(i: number, fallback: T): T => {
      const r = query.data?.[i]
      return r?.status === 'success' && r.result !== undefined ? (r.result as T) : fallback
    }

    const paySymbol = at<string>(2, 'USDT')

    /*
     * Display symbol, overridden ahead of the contract.
     *
     * The token is branded bNDX, but its ERC-20 symbol is fixed at construction —
     * `ERC20(name, symbol)` with no setter — so the deployed contract still answers
     * "NDX" and will until it is redeployed. Everything the app renders goes through
     * this one field, so the rebrand lives here rather than in seventy string
     * literals that would drift apart.
     *
     * The divergence is deliberate but REAL: a wallet, BscScan, or anyone reading the
     * contract directly still sees NDX. Wherever the app points a user at the chain to
     * verify something, it has to say so. `onChainSymbol` below is what those places
     * should use.
     */
    const onChainSymbol = at<string>(0, 'NDX')
    const ndxSymbol = 'bNDX'

    const ndx: TokenMeta = {
      address: CONTRACTS.ndx,
      name: ndxSymbol,
      symbol: ndxSymbol,
      decimals: Number(at<number>(1, 18)),
    }
    const payment: TokenMeta = {
      address: CONTRACTS.usdt,
      name: paySymbol,
      symbol: paySymbol,
      decimals: Number(at<number>(3, 18)),
    }

    return {
      ndx,
      payment,
      /** What the contract itself reports — for anything that sends a user on-chain. */
      onChainSymbol,
      isLoading: query.isLoading,
      /** `1,234.5 NDX` */
      fmtNdx: (v: bigint, digits?: number) =>
        `${formatAmount(v, ndx.decimals, digits)} ${ndx.symbol}`,
      /** `1,234.5 USDT` */
      fmtPay: (v: bigint, digits?: number) =>
        `${formatAmount(v, payment.decimals, digits)} ${payment.symbol}`,
      /** Bare numbers, for when the unit is already in a label. */
      ndxAmount: (v: bigint, digits?: number) => formatAmount(v, ndx.decimals, digits),
      payAmount: (v: bigint, digits?: number) => formatAmount(v, payment.decimals, digits),
      ndxCompact: (v: bigint) => formatCompact(v, ndx.decimals),
      payCompact: (v: bigint) => formatCompact(v, payment.decimals),
      /** Raw bigint → float, for arithmetic that has to leave bigint space. */
      toNumberNdx: (v: bigint) => Number(v) / 10 ** ndx.decimals,
      toNumberPay: (v: bigint) => Number(v) / 10 ** payment.decimals,
    }
  }, [query.data, query.isLoading])
}

export type Tokens = ReturnType<typeof useTokens>

export { FALLBACK as fallbackToken }
